/**
 * Created by neo on 11.01.2026
 * Team Browse Modal - Modal for browsing and joining teams
 */
import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from '@emotion/styled';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useToast } from '../../../Hooks/useToast';
import { RouteChallengeTeam, RouteChallengeTeamApi } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { Input } from '../../../Components/Input';
import { Button } from '../../../Components/Button';
import { LoadingContainer } from '../../../Components/LoadingContainer';
import { getMediaMedium } from '../../../Model/Media/Media';
import { Theme } from '../../../Theme/Theme';

export type TeamBrowseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  challengeId: string;
  onTeamJoined: (team: RouteChallengeTeam) => void;
};

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 16px;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
`;

const TeamsGrid = styled.div`
  display: grid;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
`;

const TeamCard = styled.div`
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const TeamImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const TeamImagePlaceholder = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const TeamInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TeamName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${Theme.Colors.textColor};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PrivateIcon = styled.span`
  font-size: 14px;
`;

const TeamMeta = styled.div`
  font-size: 13px;
  color: ${Theme.Colors.warmGrey};
`;

const JoinButton = styled(Button)`
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 24px;
  color: ${Theme.Colors.charcoal};
`;

const EmptyEmoji = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${Theme.Colors.lighterGrey};
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const InviteCodeInput = styled(Input)`
  margin-top: 8px;
`;

const InviteCodeContainer = styled.div`
  margin-top: 8px;
`;

export const TeamBrowseModal: React.FC<TeamBrowseModalProps> = observer(
  ({ isOpen, onClose, challengeId, onTeamJoined }) => {
    const { t } = useTrans();
    const toast = useToast();

    const [searchQuery, setSearchQuery] = useState('');
    const [teams, setTeams] = useState<RouteChallengeTeam[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isJoining, setIsJoining] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [inviteCodeMap, setInviteCodeMap] = useState<Record<string, string>>({});
    const [showInviteCodeFor, setShowInviteCodeFor] = useState<string | null>(null);

    const pageSize = 20;

    const fetchTeams = useCallback(
      async (pageNum: number, query: string) => {
        setIsLoading(true);
        try {
          const fetchedTeams = await RouteChallengeTeamApi.find({
            challengeId,
            query: query || undefined,
            page: pageNum,
            size: pageSize,
          });

          setTeams(fetchedTeams);
          setHasMore(fetchedTeams.length === pageSize);
        } catch (error) {
          console.error('Error fetching teams:', error);
          toast.showToast(
            t('team.browse.error.fetchFailed', { defaultValue: 'Failed to load teams' }),
            'error',
          );
        } finally {
          setIsLoading(false);
        }
      },
      [challengeId, toast, t],
    );

    useEffect(() => {
      if (isOpen) {
        fetchTeams(0, searchQuery);
        setPage(0);
      }
    }, [isOpen, searchQuery, fetchTeams]);

    const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setPage(0);
    }, []);

    const handleJoin = useCallback(
      async (team: RouteChallengeTeam) => {
        if (team.isPrivate && !inviteCodeMap[team.id]) {
          setShowInviteCodeFor(team.id);
          return;
        }

        setIsJoining(team.id);
        try {
          await team.join(inviteCodeMap[team.id] || undefined);
          toast.showToast(
            t('team.join.success', { defaultValue: 'Successfully joined team!' }),
            'success',
          );
          onTeamJoined(team);
          onClose();
        } catch (error) {
          console.error('Error joining team:', error);
          toast.showToast(
            t('team.join.error.failed', {
              defaultValue: 'Failed to join team. Please check your invite code and try again.',
            }),
            'error',
          );
        } finally {
          setIsJoining(null);
        }
      },
      [inviteCodeMap, toast, t, onTeamJoined, onClose],
    );

    const handleInviteCodeChange = useCallback((teamId: string, code: string) => {
      setInviteCodeMap((prev) => ({ ...prev, [teamId]: code }));
    }, []);

    const handlePrevious = useCallback(() => {
      if (page > 0) {
        const newPage = page - 1;
        setPage(newPage);
        fetchTeams(newPage, searchQuery);
      }
    }, [page, searchQuery, fetchTeams]);

    const handleNext = useCallback(() => {
      if (hasMore) {
        const newPage = page + 1;
        setPage(newPage);
        fetchTeams(newPage, searchQuery);
      }
    }, [hasMore, page, searchQuery, fetchTeams]);

    return (
      <StyledModal isOpen={isOpen} toggle={onClose} size="lg" centered>
        <ModalHeader toggle={onClose}>
          {t('team.browse.title', { defaultValue: 'Browse Teams' })}
        </ModalHeader>
        <ModalBody>
          <SearchContainer>
            <Input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={t('team.browse.searchPlaceholder', { defaultValue: 'Search teams...' })}
            />
          </SearchContainer>

          {isLoading ? (
            <LoadingContainer text={t('team.browse.loading', { defaultValue: 'Loading teams...' })} />
          ) : teams.length === 0 ? (
            <EmptyState>
              <EmptyEmoji>🔍</EmptyEmoji>
              <EmptyText>
                {t('team.browse.empty', { defaultValue: 'No teams found. Create one!' })}
              </EmptyText>
            </EmptyState>
          ) : (
            <>
              <TeamsGrid>
                {teams.map((team) => (
                  <div key={team.id}>
                    <TeamCard>
                      {team.image ? (
                        <TeamImage src={getMediaMedium(team.image)} alt={team.name} />
                      ) : (
                        <TeamImagePlaceholder>👥</TeamImagePlaceholder>
                      )}
                      <TeamInfo>
                        <TeamName>
                          {team.name}
                          {team.isPrivate && <PrivateIcon>🔒</PrivateIcon>}
                        </TeamName>
                        <TeamMeta>
                          {team.memberCount} / {team.maxAllowedMembers}{' '}
                          {t('team.browse.members', { defaultValue: 'members' })}
                        </TeamMeta>
                      </TeamInfo>
                      <JoinButton
                        size="sm"
                        color="primary"
                        onClick={() => handleJoin(team)}
                        disabled={
                          !team.hasSeatsAvailable || isJoining === team.id
                        }
                      >
                        {isJoining === team.id
                          ? t('team.browse.joining', { defaultValue: 'Joining...' })
                          : !team.hasSeatsAvailable
                          ? t('team.browse.full', { defaultValue: 'Full' })
                          : t('team.browse.join', { defaultValue: 'Join' })}
                      </JoinButton>
                    </TeamCard>
                    {team.isPrivate && showInviteCodeFor === team.id && (
                      <InviteCodeContainer>
                        <InviteCodeInput
                          type="text"
                          value={inviteCodeMap[team.id] || ''}
                          onChange={(e) => handleInviteCodeChange(team.id, e.target.value)}
                          placeholder={t('team.browse.inviteCodePlaceholder', {
                            defaultValue: 'Enter invite code',
                          })}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleJoin(team);
                            }
                          }}
                        />
                      </InviteCodeContainer>
                    )}
                  </div>
                ))}
              </TeamsGrid>

              {(page > 0 || hasMore) && (
                <PaginationContainer>
                  <Button size="sm" color="secondary" onClick={handlePrevious} disabled={page === 0}>
                    {t('common.previous', { defaultValue: 'Previous' })}
                  </Button>
                  <PageInfo>
                    {t('common.page', { defaultValue: 'Page' })} {page + 1}
                  </PageInfo>
                  <Button size="sm" color="secondary" onClick={handleNext} disabled={!hasMore}>
                    {t('common.next', { defaultValue: 'Next' })}
                  </Button>
                </PaginationContainer>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClose}>
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        </ModalFooter>
      </StyledModal>
    );
  },
);
